<template>
  <Box>
    <BoxHead
      icon="fas fa-folder-open"
      :link="link"
    >
      Documents
    </BoxHead>

    <LoaderWithText
      v-if="isFetching"
      size="3_5"
    />

    <div
      v-else
      class="documents-body"
    >
      <Stack
        v-if="topDocuments.length"
        gap="2"
        class="tw-grow"
      >
        <Stack
          gap="1"
          class="document-items"
        >
          <router-link
            v-for="doc in topDocuments"
            :key="doc.id"
            :to="{
              name: 'document-view',
              params: { id: doc.external_id },
              query: { entityId: entityExternalId }
            }"
            target="_blank"
            class="document-item"
          >
            <p class="document-filename">
              {{ doc.filename }}
            </p>
            <p class="document-classification">
              {{ doc.classification }}
            </p>
            <Icon
              class="document-icon"
              :class="docIconColor(doc)"
              :icon="docIcon(doc)"
            />
          </router-link>
        </Stack>

        <div
          v-if="viewMore"
          style="margin-top: auto;"
        >
          <Button
            icon-before="fas fa-plus"
            :to="toPath"
            size="3_5"
            spacing="tight"
          >
            {{ viewMore }}
          </Button>
        </div>
      </Stack>

      <EmptyBox
        v-else
        size="3_5"
      >
        <Stack gap="2">
          <p>No documents found</p>
          <Button
            :to="toPath"
            icon-before="fas fa-upload"
            spacing="tight"
          >
            Upload Documents
          </Button>
        </Stack>
      </EmptyBox>
    </div>
  </Box>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Box, BoxHead, Button, EmptyBox, Icon, LoaderWithText, Stack } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';

const advisorStore = useAdvisorStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});

const isFetching = ref(false);
const hasFetched = ref(false);
const documents = ref([]);

const toPath = { name: 'entity-documents' };
const link = computed(() => {
  return {
    to: toPath,
    label: 'View all',
  };
});

const docIcon = doc => {
  const passed = Array.isArray(doc?.validation) && doc.validation[0]?.passed;
  return passed ? 'fas fa-check-circle' : 'fas fa-times';
};

const docIconColor = doc => {
  const passed = Array.isArray(doc?.validation) && doc.validation[0]?.passed;
  return passed ? 'stk-color-success-60' : 'stk-color-danger-40';
};

const topDocuments = computed(() => documents.value.slice(0, 10));
const viewMore = computed(() => documents.value.length > 10 ? `View ${documents.value.length - 10} more` : '');

const fetchData = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(`/api/v1/advisor-documents/${props.entityExternalId}/`, {
      method: 'GET',
      headers: advisorStore.headers,
    });


    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    const data = await response.json();
    documents.value = data.documents || [];
  } catch (error) {
    console.error(`Error fetching`, error);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.documents-body {
  display: flex;
  flex-grow: 1;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 0.25em;
  cursor: ne-resize;
  color: var(--stk-text-strong);
  transition-duration: var(--stk-transition-duration);
  transition-property: color;

  &:hover {
    color: var(--stk-primary);
  }
}

.document-filename {
  font-size: 0.875em;
}

.document-classification {
  color: var(--stk-text-weak);
  font-size: 0.75em;
  margin-left: auto;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: right;
  max-width: 10em;
  overflow: hidden;
}

.document-icon {
  font-size: 0.75em;
}
</style>
